import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const AuthContext = createContext(null);

// API-Basis-URL — identisch mit appConfig.apiBaseUrl in config.js.
// Wird lazy geladen damit AuthContext keinen zyklischen Import erzeugt.
let _apiBase = null;
async function getApiBase() {
  if (_apiBase) return _apiBase;
  try {
    const { appConfig } = await import('../config');
    _apiBase = appConfig.apiBaseUrl || 'https://app.hejvi.de/api';
  } catch {
    _apiBase = 'https://app.hejvi.de/api';
  }
  return _apiBase;
}

const LS_TOKEN   = 'hejvi_access_token';
const LS_REFRESH = 'hejvi_refresh_token';
const LS_USER    = 'hejvi_user';
const LS_FEAT    = 'hejvi_features';
const LS_EXPIRY  = 'hejvi_token_expiry';

// Standardmäßig alle Features false
const DEFAULT_FEATURES = {
  // Learning
  courses: false, challenges: false, favourites: false, achievements: false,
  download_document: false, leaderboard: false,
  // Generative
  video_builder: false, element_creator: false, tts: false,
  quiz_builder: false, course_builder: false,
  // Intelligence
  frag_hejvi: false, document_chat: false,
  domain_knowledge: false, company_knowledge: false,
  // Admin (group-gesteuert)
  admin_panel: false, user_management: false, group_management: false,
  content_management: false, content_approval: false, analytics: false,
  knowledge_base: false, super_admin: false,
  // Admin-Tab-Freigaben (group 0/2/3 via admin_role_config + plan, vom Backend gesetzt)
  // Steuern welche Tabs in AdminPanel sichtbar sind — unabhängig von user-seitigen Plan-Features
  // group 0 (Tenant-SuperAdmin): alle Tabs konfigurierbar
  // group 3 (Tenant-Admin):      Basis + plan-gated konfigurierbar
  // group 2 (Supervisor):        nur plan-gated konfigurierbar (kein user/group management)
  admin_user_management: false,
  admin_group_management: false,
  admin_content_management: false,
  admin_content_approval: false,
  admin_analytics: false,
  admin_knowledge_base: false,
  admin_frag_hejvi: false,
  admin_document_chat: false,
  admin_domain_knowledge: false,
  admin_notifications: false,
  admin_company_knowledge: false,
};

function parseFeatures(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_FEATURES };
  // Features können als flaches Objekt oder als verschachteltes {learning:{}, generative:{}, intelligence:{}, admin:{}} kommen
  const flat = { ...DEFAULT_FEATURES };
  const merge = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    Object.entries(obj).forEach(([k, v]) => {
      if (typeof v === 'boolean') {
        flat[k] = v;
      } else if (typeof v === 'object') {
        merge(v);
      }
    });
  };
  merge(raw);
  return flat;
}

function readStoredFeatures() {
  try {
    const raw = localStorage.getItem(LS_FEAT);
    if (!raw) return { ...DEFAULT_FEATURES };
    return parseFeatures(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_FEATURES };
  }
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem(LS_USER);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [token, setToken]         = useState(() => localStorage.getItem(LS_TOKEN) || null);
  const [user, setUser]           = useState(readStoredUser);
  const [features, setFeatures]   = useState(readStoredFeatures);
  const [uiLanguages, setUiLanguages] = useState([]);
  const refreshLockRef  = useRef(false);
  // Verhindert, dass der /user-Fetch durch doRefresh-ausgelöste Token-State-Änderungen
  // mehrfach gleichzeitig feuert. Wird beim ersten erfolgreichen Fetch auf true gesetzt
  // und bei logout() zurückgesetzt.
  const userFetchedRef  = useRef(false);
  // Ref auf logout() — damit makeAuthenticatedRequest logout aufrufen kann,
  // ohne zirkuläre useCallback-Abhängigkeiten zu erzeugen.
  const logoutRef = useRef(null);

  // ───────────────────────────────────────────────
  // Token-Refresh
  // ───────────────────────────────────────────────
  const doRefresh = useCallback(async () => {
    if (refreshLockRef.current) return null;
    const refreshToken = localStorage.getItem(LS_REFRESH);
    if (!refreshToken) return null;
    refreshLockRef.current = true;
    try {
      const apiBase = await getApiBase();
      const res = await fetch(`${apiBase}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) throw new Error('refresh failed');
      const data = await res.json();
      const newToken = data.access_token || data.token;
      const newExpiry = data.expires_at || (Date.now() / 1000 + (data.expires_in || 3600));
      localStorage.setItem(LS_TOKEN, newToken);
      localStorage.setItem(LS_EXPIRY, String(newExpiry));
      if (data.refresh_token) localStorage.setItem(LS_REFRESH, data.refresh_token);
      // KEIN setToken() — würde useEffect([token]) neu triggern → /user-Loop.
      // Alle nachfolgenden API-Calls lesen den neuen Token direkt aus localStorage.
      // Features aus Refresh-Response sofort übernehmen (Plan-/Group-Änderungen ohne Re-Login)
      if (data.features) {
        const flat = parseFeatures(data.features);
        localStorage.setItem(LS_FEAT, JSON.stringify(flat));
        setFeatures(flat);
      }
      return newToken;
    } catch {
      return null;
    } finally {
      refreshLockRef.current = false;
    }
  }, []);

  // Proaktiver Pre-Expiry-Refresh (60 Sek. vor Ablauf)
  const getValidToken = useCallback(async () => {
    const expiry = parseFloat(localStorage.getItem(LS_EXPIRY) || '0');
    const now = Date.now() / 1000;
    if (expiry && expiry - now < 60) {
      const refreshed = await doRefresh();
      return refreshed || localStorage.getItem(LS_TOKEN);
    }
    return localStorage.getItem(LS_TOKEN);
  }, [doRefresh]);

  // ───────────────────────────────────────────────
  // makeAuthenticatedRequest — gibt pre-parsed JSON zurück
  // WICHTIG: Aufrufer darf KEIN .json() mehr aufrufen
  // ───────────────────────────────────────────────
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    let currentToken = await getValidToken();

    // Relative URLs (z.B. '/user') mit API-Basis prefixen.
    // Absolute URLs (http/https) werden unverändert verwendet.
    const apiBase = await getApiBase();
    const fullUrl = url.startsWith('http') ? url : `${apiBase}${url}`;

    const doFetch = async (tkn) => {
      // Content-Type nur setzen wenn kein FormData-Body — sonst setzt der Browser
      // automatisch multipart/form-data mit korrekter boundary.
      const isFormData = options.body instanceof FormData;
      const headers = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(options.headers || {}),
        ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
      };
      return fetch(fullUrl, { ...options, headers });
    };

    let res = await doFetch(currentToken);

    // 401 → Token-Refresh versuchen
    if (res.status === 401) {
      const refreshed = await doRefresh();
      if (refreshed) {
        currentToken = refreshed;
        res = await doFetch(currentToken);
      } else {
        // Refresh fehlgeschlagen (Token + Refresh-Token abgelaufen) → Session beenden.
        // logoutRef statt direktem logout()-Aufruf, um zirkuläre useCallback-Deps zu vermeiden.
        logoutRef.current?.();
        const err = new Error('Unauthorized');
        err.status = 401;
        throw err;
      }
    }

    // Antwort als JSON parsen und zurückgeben
    // Fehler bei nicht-OK Responses ebenfalls als geparsten Body zurückgeben
    // (damit Aufrufer error.message lesen kann)
    let data;
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = { message: text }; }
    }

    if (!res.ok) {
      const err = new Error(data?.error || data?.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }

    // Kompatibilitäts-Shim: Einige ältere Komponenten rufen noch .json() auf dem
    // Rückgabewert auf (erwarten ein Response-Objekt). Da makeAuthenticatedRequest
    // pre-parsed JSON zurückgibt, würde das mit "t.json is not a function" crashen.
    // Der Shim fängt das ab und gibt data unverändert zurück — kein Datenverlust,
    // kein Crash. Langfristig: Komponenten auf direkten Datenzugriff umstellen.
    if (data && typeof data === 'object' && !data.json) {
      data.json = () => Promise.resolve(data);
      data.ok   = true;
    }
    return data;
  }, [getValidToken, doRefresh]);

  // ───────────────────────────────────────────────
  // /user fetch beim Mount — holt aktuellen Nutzer + Features
  //
  // Läuft nur einmal pro Login-Session (userFetchedRef als Guard).
  // doRefresh() setzt KEIN setToken() mehr → useEffect feuert nicht
  // mehrfach → kein /user-Loop.
  // ───────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    if (userFetchedRef.current) return; // Bereits gefetcht — kein erneuter Aufruf
    userFetchedRef.current = true;

    const storedUser = readStoredUser();
    if (storedUser) setUser(storedUser);

    (async () => {
      try {
        const data = await makeAuthenticatedRequest('/user');
        if (data?.user) {
          setUser(data.user);
          localStorage.setItem(LS_USER, JSON.stringify(data.user));
        }
        if (data?.features) {
          const flat = parseFeatures(data.features);
          setFeatures(flat);
          localStorage.setItem(LS_FEAT, JSON.stringify(flat));
        }
        if (data?.ui_languages) {
          setUiLanguages(data.ui_languages);
        }
      } catch (e) {
        if (e.status === 401) {
          userFetchedRef.current = false; // Bei 401 zurücksetzen, damit nach Re-Login neu fetcht
          logout();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ───────────────────────────────────────────────
  // hejvi:features-updated — externes Features-Update (z.B. nach Token-Refresh
  // aus AdminPanel.js oder anderen Komponenten) sofort in den Context übernehmen
  // ───────────────────────────────────────────────
  useEffect(() => {
    const onFeaturesUpdated = (e) => {
      if (!e.detail || typeof e.detail !== 'object') return;
      const flat = parseFeatures(e.detail);
      localStorage.setItem(LS_FEAT, JSON.stringify(flat));
      setFeatures(flat);
    };
    window.addEventListener('hejvi:features-updated', onFeaturesUpdated);
    return () => window.removeEventListener('hejvi:features-updated', onFeaturesUpdated);
  }, []);

  // ───────────────────────────────────────────────
  // Login — speichert Token + User + Features
  // ───────────────────────────────────────────────
  const login = useCallback((response) => {
    const accessToken = response.access_token || response.token;
    const refreshToken = response.refresh_token;
    const expiry = response.expires_at || (Date.now() / 1000 + (response.expires_in || 3600));
    const userData = response.user;
    const featData = response.features;

    if (accessToken) {
      localStorage.setItem(LS_TOKEN, accessToken);
      localStorage.setItem(LS_EXPIRY, String(expiry));
      setToken(accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(LS_REFRESH, refreshToken);
    }
    if (userData) {
      localStorage.setItem(LS_USER, JSON.stringify(userData));
      setUser(userData);
    }
    if (featData) {
      const flat = parseFeatures(featData);
      localStorage.setItem(LS_FEAT, JSON.stringify(flat));
      setFeatures(flat);
    }
    if (response.ui_languages) {
      setUiLanguages(response.ui_languages);
    }
  }, []);

  // ───────────────────────────────────────────────
  // Logout
  // ───────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_FEAT);
    localStorage.removeItem(LS_EXPIRY);
    userFetchedRef.current = false; // Nach Logout wieder fetch-bereit für nächsten Login
    setToken(null);
    setUser(null);
    setFeatures({ ...DEFAULT_FEATURES });
    setUiLanguages([]);
  }, []);

  // logoutRef immer aktuell halten — für makeAuthenticatedRequest
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  // ───────────────────────────────────────────────
  // Visibility-Check — "Banking-App"-Verhalten:
  // Wenn der Nutzer nach längerer Abwesenheit zur App zurückkehrt
  // (Laptop aufklappen, Tab wechseln), wird das Token sofort geprüft.
  // Ist es abgelaufen und der Refresh schlägt fehl → direkt logout().
  // ───────────────────────────────────────────────
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      const storedToken = localStorage.getItem(LS_TOKEN);
      if (!storedToken) return; // Bereits ausgeloggt

      const expiry = parseFloat(localStorage.getItem(LS_EXPIRY) || '0');
      const now    = Date.now() / 1000;

      // Token abgelaufen oder läuft in weniger als 60 Sek. ab → Refresh versuchen
      if (expiry && expiry - now < 60) {
        const refreshed = await doRefresh();
        if (!refreshed) {
          // Refresh fehlgeschlagen → Session abgelaufen → sofort abmelden
          logout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [doRefresh, logout]);

  // ───────────────────────────────────────────────
  // hasFeature(key) — boolean Helper
  // Unterstützt dot-notation: hasFeature('generative.video_builder')
  // ───────────────────────────────────────────────
  const hasFeature = useCallback((key) => {
    if (!key) return false;
    if (key.includes('.')) {
      const parts = key.split('.');
      // Bei verschachtelter Notation direkt im features-Objekt suchen
      return !!features[parts[parts.length - 1]];
    }
    return !!features[key];
  }, [features]);

  // ───────────────────────────────────────────────
  // hasAdminTab(key) — prüft ob ein Admin-Tab sichtbar sein soll
  // Kombiniert: features.admin_{key} (vom Backend für group 0/3 via role_config)
  //         ODER features.{key} direkt (für group 2 / Fallback)
  // Beispiel: hasAdminTab('frag_hejvi')
  //   → true wenn features.admin_frag_hejvi === true  (group 0/3)
  //      OR   features.frag_hejvi === true             (group 2 Fallback)
  // ───────────────────────────────────────────────
  const hasAdminTab = useCallback((key) => {
    if (!key) return false;
    return !!features['admin_' + key] || !!features[key];
  }, [features]);

  // ───────────────────────────────────────────────
  // API-Convenience-Funktionen
  // Werden von Dashboard, Profile und anderen Komponenten via useAuth() genutzt.
  // Alle geben pre-parsed JSON zurück (wie makeAuthenticatedRequest).
  // ───────────────────────────────────────────────

  /** Aktuellen Nutzer + Features vom Backend holen */
  const getUser = useCallback(async () => {
    return makeAuthenticatedRequest('/user');
  }, [makeAuthenticatedRequest]);

  /** Feed-Elemente laden — optionale Filter: page, tags, mandatory, favourites_only */
  const getFeed = useCallback(async (params = {}) => {
    const q = new URLSearchParams();
    if (params.page)             q.set('page',            String(params.page));
    if (params.tags?.length)     q.set('tags',            params.tags.join(','));
    if (params.mandatory)        q.set('mandatory',       String(params.mandatory));
    if (params.favourites_only)  q.set('favourites_only', String(params.favourites_only));
    const qs = q.toString();
    return makeAuthenticatedRequest(qs ? `/feed?${qs}` : '/feed');
  }, [makeAuthenticatedRequest]);

  /** Favoriten-Status eines Elements umschalten.
   *  Backend: POST /toggle-favourites-post  { hash_id }
   *  Alternativ: GET /toggle-favourites/{hash} (Legacy-Pfad) */
  const toggleFavourites = useCallback(async (elementId) => {
    return makeAuthenticatedRequest('/toggle-favourites-post', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ hash_id: elementId }),
    });
  }, [makeAuthenticatedRequest]);

  /** Lernfortschritt für ein Element setzen (hash_id, 0–100) */
  const setProgress = useCallback(async (elementId, progress) => {
    return makeAuthenticatedRequest('/set-progress', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ element_id: elementId, progress }),
    });
  }, [makeAuthenticatedRequest]);

  /** Volltext-Suche — term + optionale Parameter (limit, page, …) */
  const searchByTerm = useCallback(async (term, params = {}) => {
    const q = new URLSearchParams({ term, ...Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    )});
    return makeAuthenticatedRequest(`/byterm?${q.toString()}`);
  }, [makeAuthenticatedRequest]);

  /** Meine Kurse + Sammlungen laden (MyCourses-Tab) */
  const getMyCourses = useCallback(async () => {
    return makeAuthenticatedRequest('/mycourses');
  }, [makeAuthenticatedRequest]);

  /** Favoriten des Nutzers laden */
  const getFavourites = useCallback(async () => {
    return makeAuthenticatedRequest('/favourites');
  }, [makeAuthenticatedRequest]);

  /** Challenges laden — optionale Filter (tags:[]) */
  const getChallenges = useCallback(async (params = {}) => {
    const q = new URLSearchParams();
    if (params.tags?.length) q.set('tags', params.tags.join(','));
    const qs = q.toString();
    return makeAuthenticatedRequest(qs ? `/challenges?${qs}` : '/challenges');
  }, [makeAuthenticatedRequest]);

  /** Aktuelle (laufende) Challenges des Nutzers laden */
  const getCurrentChallenges = useCallback(async () => {
    return makeAuthenticatedRequest('/current-challenges');
  }, [makeAuthenticatedRequest]);

  /** Element-Detail laden (ElementPage) */
  const getElement = useCallback(async (hashId) => {
    return makeAuthenticatedRequest(`/element/${hashId}`);
  }, [makeAuthenticatedRequest]);

  // ───────────────────────────────────────────────
  // setSettings — Einstellungen aktualisieren (User-Objekt)
  // ───────────────────────────────────────────────
  const setSettings = useCallback((updater) => {
    setUser(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      localStorage.setItem(LS_USER, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = {
    // Auth-State
    token,
    user,
    features,
    uiLanguages,
    // Methoden
    login,
    logout,
    hasFeature,
    hasAdminTab,
    makeAuthenticatedRequest,
    getValidToken,
    setSettings,
    // API-Convenience (für Dashboard, Profile, MyCourses, Challenges, etc.)
    getUser,
    getFeed,
    getMyCourses,
    getFavourites,
    getChallenges,
    getCurrentChallenges,
    toggleFavourites,
    getElement,
    setProgress,
    searchByTerm,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  return ctx;
}

// Named Exports für direkten Zugriff (Kompatibilität)
export default AuthContext;
